/*-----------------------------------------------------------------------------/
/ Cart limit: maks. 2 stk. i alt af 100 x 140 cm med ramme pr. ordre
/ - Gruppen genkendes på SKU-suffikset -100140-EG / -100140-SORT (pakker SET* undtaget),
/   så den ikke afhænger af rammens navn ("Massiv Egetræsramme" omdøbes 1/11)
/ - Tjekker Cart.add (læg i kurv) og Cart.change (antal i kurv/cart-drawer)
/ - Skjuler "Køb nu" (dynamic checkout) på varianter i gruppen
/-----------------------------------------------------------------------------*/
import Cart, { CartStore } from "cart-store";

const MAX_QTY = 2;

const message = () => window.theme.localize("CART_LIMIT_100X140");

// Gruppen: 100 x 140 med egetræs- eller sort ramme. Pakker (SKU SET*, "2 Stk.", "3 stk.") er ikke med.
// Primært SKU-suffikset; varianttitlen bruges kun, hvis SKU mangler.
const LIMITED_SKU = /-100140-(EG|SORT)$/i;
const PACK_SKU = /^SET/i;

const isLimited = ({sku: sku = "", title: title = ""} = {}) => {
    if (sku) return LIMITED_SKU.test(String(sku).trim()) && !PACK_SKU.test(String(sku).trim());
    const t = String(title).toLowerCase();
    const size = t.includes("100 x 140") || t.includes("100x140");
    const frame = t.includes("egetræsramme") || t.includes("træramme");
    const pack = /(^|[^a-zæøå])stk\b/.test(t);
    return size && frame && !pack && !t.includes("ingen ramme");
};

const isLimitedItem = item => isLimited({
    sku: item.sku,
    title: item.variant_title
});

const groupQty = (cart, excludeKey = null) => (cart.items || []).reduce(((sum, item) => item.key !== excludeKey && isLimitedItem(item) ? sum + item.quantity : sum), 0);

// Variant (sku + titel) fra produktdata på siden (produktside, quick shop, billedvæg). null = ukendt.
const findVariant = id => {
    const variantId = Number(id);
    const sources = document.querySelectorAll('variant-selects script[type="application/json"], variant-radios script[type="application/json"], variant-mixed-inputs script[type="application/json"], script[id^="ProductJson-"]');
    for (const source of sources) {
        try {
            const data = JSON.parse(source.textContent);
            const variants = Array.isArray(data) ? data : data.variants || [];
            const variant = variants.find((v => v.id === variantId));
            if (variant) return {
                sku: variant.sku || "",
                title: variant.title || ""
            };
        } catch (e) {}
    }
    return null;
};

const originalAdd = Cart.add.bind(Cart);

Cart.add = async variants => {
    const items = Array.isArray(variants) ? variants : [ variants ];
    let allKnown = true;
    let adding = 0;
    items.forEach((item => {
        const variant = findVariant(item.id);
        if (variant === null) {
            allKnown = false;
        } else if (isLimited(variant)) {
            adding += Number(item.quantity || 1);
        }
    }));
    if (allKnown) {
        if (adding > 0) {
            const cart = await CartStore.getCart();
            if (groupQty(cart) + adding > MAX_QTY) throw new Error(message());
        }
        return originalAdd(variants);
    }
    // Ukendt variant: læg i kurv, og rul tilbage hvis grænsen blev overskredet
    const before = await CartStore.getCart();
    const cart = await originalAdd(variants);
    if (groupQty(cart) > MAX_QTY && groupQty(cart) > groupQty(before)) {
        // Linjenøgler skifter, når Shopify fordeler rabatter om, og en linje kan blive delt i flere.
        // Sammenlign derfor på variant + egenskaber, og rul tilbage i ét kald med de aktuelle nøgler.
        const lineId = i => `${i.id}|${JSON.stringify(i.properties || {})}`;
        const sumBy = items => items.filter(isLimitedItem).reduce(((m, i) => m.set(lineId(i), (m.get(lineId(i)) || 0) + i.quantity)), new Map);
        const prev = sumBy(before.items);
        const updates = {};
        sumBy(cart.items).forEach(((qty, id) => {
            let excess = qty - (prev.get(id) || 0);
            for (const item of cart.items.filter((i => lineId(i) === id))) {
                if (excess <= 0) break;
                const remove = Math.min(excess, item.quantity);
                updates[item.key] = item.quantity - remove;
                excess -= remove;
            }
        }));
        if (Object.keys(updates).length) await CartStore.updateCart({
            updates: updates
        });
        await Cart.refresh();
        throw new Error(message());
    }
    return cart;
};

const originalChange = Cart.change.bind(Cart);

Cart.change = async requestBody => {
    const quantity = Number(requestBody.quantity);
    const cart = await CartStore.getCart();
    const item = cart.items.find((i => i.key === requestBody.id));
    if (item && isLimitedItem(item) && quantity > item.quantity && groupQty(cart, item.key) + quantity > MAX_QTY) {
        // Opdater kurv-state, så mængdefeltet hopper tilbage til det faktiske antal
        await Cart.refresh();
        throw new Error(message());
    }
    return originalChange(requestBody);
};

// Skjul "Køb nu" på varianter i gruppen
const syncDynamicButton = form => {
    if (!form) return;
    const dynamicButton = form.querySelector("dynamic-button");
    const idInput = form.querySelector('input[name="id"]');
    if (!dynamicButton || !idInput) return;
    const variant = findVariant(idInput.value);
    const hide = variant !== null && isLimited(variant);
    dynamicButton.hidden = hide;
    dynamicButton.style.display = hide ? "none" : "";
    // Uden "Køb nu" skal "Læg i kurv" være primærknap
    const buttons = form.querySelector(".js-product-buttons");
    const addButton = form.querySelector('[name="add"]');
    if (buttons) buttons.classList.toggle("product-form__add--dynamic", !hide);
    if (addButton) {
        addButton.classList.toggle("c-btn--primary", hide);
        addButton.classList.toggle("c-btn--hollow", !hide);
    }
};

const syncAll = (root = document) => {
    root.querySelectorAll("form.js-product-form").forEach(syncDynamicButton);
};

document.addEventListener("change", (e => {
    if (e.target.matches && e.target.matches('input[name="id"]')) syncDynamicButton(e.target.form);
}));

// Quick shop indsætter produktformularer dynamisk
new MutationObserver((mutations => {
    mutations.forEach((mutation => {
        mutation.addedNodes.forEach((node => {
            if (node.nodeType !== 1) return;
            if (node.matches("form.js-product-form")) {
                syncDynamicButton(node);
            } else if (node.querySelector("form.js-product-form")) {
                syncAll(node);
            }
        }));
    }));
})).observe(document.body, {
    childList: true,
    subtree: true
});

syncAll();
